import React, { Component } from 'react';
import moment from 'moment';
import { Spin, Tooltip } from 'antd';
import * as Constants from '../../../common/Global/constants';
import { getLocalStorageVariables, handleTokenExpiry } from '../../../common/Global/commonFunctions';
import FilterDropDown from '../../../common/DropdownMenus/FilterDropdown';
import ReactSelectDropdown from '../../../common/DropdownMenus/ReactSelectDropdownWithSearch';

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const BAR_HEIGHT = 22;
const DATE_ROW_H = 35;
const BAR_GAP = 2;
const MIN_ROW_H = 100;
const EXECUTE_OVERLAP_COLOR = '#f5a623';

const parseExecuteResources = (item) => {
    const list = [];
    if (Array.isArray(item.memberList))
        item.memberList.forEach(r => { if (r.userName) list.push(r.userName); });
    else if (item.memberList && typeof item.memberList === 'string')
        item.memberList.split(',').forEach(m => { const n = m.split(':')[1]; if (n) list.push(n.trim()); });

    if (Array.isArray(item.executeResourceList))
        item.executeResourceList.forEach(r => { if (r.userName || r.name) list.push(r.userName || r.name); });
    else if (Array.isArray(item.executeResources))
        item.executeResources.forEach(r => { if (r.userName || r.name) list.push(r.userName || r.name); });
    else if (item.executeResources && typeof item.executeResources === 'string')
        item.executeResources.split(',').forEach(s => { const n = (s.split(':')[1] || s.split(':')[0] || '').trim(); if (n) list.push(n); });
    else
        for (let i = 1; i <= 5; i++) if (item[`L${i}UserName`]) list.push(item[`L${i}UserName`]);

    return list;
};

const parseReportResources = (item) => {
    if (Array.isArray(item.reportResourceList))
        return item.reportResourceList.map(r => r.userName || r.name || r).filter(Boolean);
    if (Array.isArray(item.reportResources))
        return item.reportResources.map(r => r.userName || r.name || r).filter(Boolean);
    if (item.reportResources && typeof item.reportResources === 'string')
        return item.reportResources.split(',').map(s => (s.split(':')[1] || s.split(':')[0] || '').trim()).filter(Boolean);
    if (!item.viewerList) return [];
    return item.viewerList.split(',').map(m => (m.split(':')[1] || '').trim()).filter(Boolean);
};

// Assignment resource strings (memberList/executeResourceList/etc.) commonly omit the
// salutation the resource-picker dropdown includes ("Mr. Ramesh Shah" vs "Ramesh Shah"),
// so strip it and allow a substring match either direction rather than requiring an
// exact match that would silently never fire.
const SALUTATION_RE = /^(mr|mrs|ms|miss|dr|prof)\.?\s+/i;
const normalizeResourceName = (name) => (name || '')
    .replace(SALUTATION_RE, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
const resourceNamesMatch = (a, b) => {
    const na = normalizeResourceName(a);
    const nb = normalizeResourceName(b);
    if (!na || !nb) return false;
    return na === nb || na.includes(nb) || nb.includes(na);
};

// Returns the selected resource's role in an assignment ('execute', 'report', 'both'), or null.
const getResourceRole = (item, resourceName) => {
    if (!resourceName) return null;
    if (!resourceName.trim()) return null;

    const isExecute = parseExecuteResources(item).some(r => resourceNamesMatch(r, resourceName));
    const isReport = parseReportResources(item).some(r => resourceNamesMatch(r, resourceName));

    if (isExecute && isReport) return 'both';
    if (isExecute) return 'execute';
    if (isReport) return 'report';
    return null;
};

const ROLE_LABELS = {
    execute: 'Execute Resource',
    report: 'Report Resource',
    both: 'Execute & Report Resource',
};

const parseUnit = (item) => {
    if (item.unitName) return item.unitName;
    if (item.unitNameCityList) return item.unitNameCityList;
    if (item.unitAddressList) return item.unitAddressList.split(':-:')[0];
    return '';
};

const parseClientName = (item) => item.clientName || item.companyName || '';

const positionEventsInWeek = (items, weekStart) => {
    const ws = weekStart.clone().startOf('day');

    const events = items.map(item => {
        const s = moment(item.startDate).startOf('day');
        const e = item.endDate ? moment(item.endDate).startOf('day') : s;
        const startCol = Math.max(0, s.diff(ws, 'days'));
        const endCol = Math.min(6, e.diff(ws, 'days'));
        const span = endCol - startCol + 1;
        const isEventStart = startCol === s.diff(ws, 'days');
        const isEventEnd = endCol === e.diff(ws, 'days');
        return { item, startCol, endCol, span, isEventStart, isEventEnd, lane: 0 };
    });

    events.sort((a, b) => {
        if (a.startCol !== b.startCol) return a.startCol - b.startCol;
        const diff = moment(a.item.startDate).diff(moment(b.item.startDate));
        if (diff !== 0) return diff;
        return b.span - a.span;
    });

    const laneEnds = [];
    events.forEach(ev => {
        let lane = 0;
        while (laneEnds[lane] !== undefined && laneEnds[lane] >= ev.startCol) lane++;
        ev.lane = lane;
        laneEnds[lane] = ev.endCol;
    });

    return events;
};

class AssignmentCalendarView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentDate: moment(),
            viewType: 'month',
            selectedDate: null,
            calendarData: [],
            loading: false,
            viewFilter: 'assignment',
            selectedResourceId: null,
            resourceList: [],
            resourceLoading: false,
        };
    }

    componentDidMount() { 
        this.fetchCalendarData(); 
    }

    componentDidUpdate(prevProps, prevState) {
        const { currentDate, viewType, viewFilter, selectedResourceId } = this.state;

        if (prevState.viewFilter !== viewFilter && viewFilter === 'resource') {
            this.fetchResourceList();
        }

        const filterSwitchedToAssignment = prevState.viewFilter !== viewFilter && viewFilter === 'assignment';
        const resourceSelected = viewFilter === 'resource' && prevState.selectedResourceId !== selectedResourceId;
        const switchedToMonth = prevState.viewType === 'week' && viewType === 'month';

        if (
            prevProps.userId !== this.props.userId ||
            !prevState.currentDate.isSame(currentDate, viewType === 'month' ? 'month' : 'isoWeek') ||
            switchedToMonth ||
            filterSwitchedToAssignment ||
            resourceSelected
        ) {
            this.fetchCalendarData();
        }
    }

    getDateRange() {
        const { currentDate, viewType } = this.state;
        if (viewType === 'month') return {
            startDate: moment(currentDate).startOf('month').format('YYYY-MM-DD'),
            endDate: moment(currentDate).endOf('month').format('YYYY-MM-DD'),
        };
        return {
            startDate: moment(currentDate).startOf('isoWeek').format('YYYY-MM-DD'),
            endDate: moment(currentDate).endOf('isoWeek').format('YYYY-MM-DD'),
        };
    }

    fetchResourceList = () => {
        const userDetails = getLocalStorageVariables();
        this.setState({ resourceLoading: true });
        fetch(Constants.GetAssignmentsCalendarViewResourceList, {
            method: 'POST', mode: 'cors',
            body: new URLSearchParams({
                email: userDetails.email,
                accessToken: userDetails.accessToken
            })
        })
        .then(res => res.json())
        .then(data => {
            if (handleTokenExpiry(data.responseCode)) return;
            if (data.responseCode === Constants.CODE_SUCCESS) {
                this.setState({ 
                    resourceList: data.data.resourceList, 
                    resourceLoading: false 
                });
            } else {
                this.setState({ 
                    resourceList: [], 
                    resourceLoading: false 
                });
            }
        })
        .catch(() => this.setState({ 
            resourceList: [], 
            resourceLoading: false 
        }));
    }

    fetchCalendarData = () => {
        const userDetails = getLocalStorageVariables();
        const { startDate, endDate } = this.getDateRange();
        const { viewFilter, selectedResourceId } = this.state;
        this.setState({ 
            loading: true 
        });

        const postParam = { 
            email: userDetails.email, 
            accessToken: userDetails.accessToken, 
            startDate, 
            endDate 
        };

        if (viewFilter === 'resource' && selectedResourceId) {
            postParam.userId = selectedResourceId;
        }

        fetch(Constants.GetAssignmentsCalendarView, { method: 'POST', mode: 'cors', body: new URLSearchParams(postParam) })
            .then(res => res.json())
            .then(data => {
                if (handleTokenExpiry(data.responseCode)) return;
                if (data.responseCode === Constants.CODE_SUCCESS) {
                    this.setState({ 
                        calendarData: data.data?.assignmentList || [], 
                        loading: false 
                    });
                } else {
                    this.setState({ 
                        calendarData: [], 
                        loading: false 
                    });
                }
            })
            .catch(() => this.setState({ 
                calendarData: [], 
                loading: false 
            }));
    }

    getEventsForWeek(weekStart, weekEnd) {
        const { calendarData } = this.state;
        const ws = weekStart.clone().startOf('day');
        const we = weekEnd.clone().startOf('day');
        return calendarData.filter(item => {
            if (!item.startDate) return false;
            const s = moment(item.startDate).startOf('day');
            const e = item.endDate ? moment(item.endDate).startOf('day') : s;
            return s.isSameOrBefore(we) && e.isSameOrAfter(ws);
        });
    }

    // Returns the overlap indicator color for a day, or null if no overlap.
    // Only shown in Assignment Wise view (Resource Wise shows overlap on the bar itself
    // instead), and only cares about shared EXECUTE resources across overlapping assignments
    // (report-resource sharing is ignored here).
    getOverlapColorForDay = (day) => {
        const { calendarData, viewFilter } = this.state;
        if (viewFilter !== 'assignment') return null;
        if (!calendarData?.length) return null;
        const d = moment(day).startOf('day');

        // get all assignments which overlap on same day
        const covering = calendarData.filter(item => {
            if (!item.startDate) return false;
            const s = moment(item.startDate).startOf('day');
            const e = item.endDate ? moment(item.endDate).startOf('day') : s;
            return d.isSameOrAfter(s) && d.isSameOrBefore(e);
        });
        if (covering.length < 2) return null;

        const executeSets = covering.map(item => {
            const set = new Set();
            parseExecuteResources(item).forEach(r => { if (r) set.add(r.trim().toLowerCase()); });
            return set;
        });

        for (let i = 0; i < executeSets.length; i++) {
            for (let j = i + 1; j < executeSets.length; j++) {
                if ([...executeSets[i]].some(r => executeSets[j].has(r))) return EXECUTE_OVERLAP_COLOR;
            }
        }
        return null;
    }

    prevPeriod = () => {
        const { currentDate, viewType } = this.state;
        this.setState({ 
            currentDate: viewType === 'month' ? moment(currentDate).subtract(1, 'month') : moment(currentDate).subtract(1, 'week') 
        });
    }

    nextPeriod = () => {
        const { currentDate, viewType } = this.state;
        this.setState({ 
            currentDate: viewType === 'month' ? moment(currentDate).add(1, 'month') : moment(currentDate).add(1, 'week') 
        });
    }

    goToToday = () => {
        this.setState({ 
            currentDate: moment(), 
            selectedDate: null 
        });
    }

    selectDate = (day) => {
        const { selectedDate } = this.state;
        this.setState({ 
            selectedDate: selectedDate && day.isSame(selectedDate, 'day') ? null : moment(day) 
        });
    }

    isSelfEngagement() {
        return this.props.company?.companyType === Constants.USER_ENGAGEMENT_TYPE_SELF;
    }

    // Name of the resource selected in "Resource Wise" view, or null (e.g. in "Assignment Wise" view).
    getSelectedResourceName() {
        const { viewFilter, selectedResourceId, resourceList } = this.state;
        if (viewFilter !== 'resource' || !selectedResourceId) return null;
        const resource = resourceList.find(r => r.userId === selectedResourceId);
        if (!resource) return null;
        return `${resource.salutation} ${resource.firstName} ${resource.lastName}`.trim();
    }

    getEventLabel(item) {
        if (this.isSelfEngagement()) return parseUnit(item) || '';
        return parseClientName(item) || parseUnit(item) || '';
    }

    renderTooltipContent(a) {
        const client = parseClientName(a);
        const unit = parseUnit(a);
        const execRes = parseExecuteResources(a);
        const repRes = parseReportResources(a);
        const resourceName = this.getSelectedResourceName();
        const isResourceWise = !!resourceName;
        // Matches the bar color logic: only set when this assignment overlaps another
        // of the same resource's assignments (not just this assignment's own role).
        const overlapRole = isResourceWise ? this.getOverlapRole(a, resourceName) : null;
        const overlapLabel = overlapRole ? ROLE_LABELS[overlapRole] : null;
        const isSelf = this.isSelfEngagement();
        return (
            <div style={{ fontSize: 12, minWidth: 220, lineHeight: 1.7 }}>
                {!isSelf && client && <div><span style={{ opacity: 0.7 }}>Client: </span><strong>{client}</strong></div>}
                {unit && <div><span style={{ opacity: 0.7 }}>Unit: </span>{unit}</div>}
                {/* {isResourceWise && overlapLabel && <div><span style={{ opacity: 0.7 }}>Overlap: </span><strong>{overlapLabel}</strong></div>} */}
                {!isResourceWise && execRes.length > 0 && <div><span style={{ opacity: 0.7 }}>Execute Resource: </span>{execRes.join(', ')}</div>}
                {!isResourceWise && repRes.length > 0 && <div><span style={{ opacity: 0.7 }}>Report Resource: </span>{repRes.join(', ')}</div>}
            </div>
        );
    }

    getThemeColor() {
        const { themeSettings } = this.props;
        return `rgba(${themeSettings.themeColor.r},${themeSettings.themeColor.g},${themeSettings.themeColor.b},${themeSettings.themeColor.a})`;
    }

    // Theme-color shade for a Resource Wise role: full strength for Execute (highest
    // priority), a lighter tint for Report, and a darkened tone for Both — all derived
    // from the app's theme color rather than unrelated fixed colors.
    // Blends a theme channel toward white (positive amount) or black (negative amount).
    // themeColor's r/g/b can arrive as strings (e.g. from stored settings), and "+" would
    // silently string-concatenate instead of add in that case, so coerce to a number first.
    static mixChannel(channel, amount) {
        const c = Number(channel) || 0;
        const mixed = amount >= 0
            ? c + (255 - c) * amount
            : c * (1 + amount);
        return Math.round(Math.min(255, Math.max(0, mixed)));
    }

    getRoleColor(role) {
        if (!role) return null;
        const { r, g, b } = this.props.themeSettings.themeColor;
        const mix = AssignmentCalendarView.mixChannel;
        // A clear light -> dark ladder (all at full opacity) so the three shades read
        // as distinct against a white background, instead of relying on alpha alone.
        // Kept modest (18%) so it never washes out to white on lighter theme colors.
        if (role === 'execute') return `rgba(${mix(r, 0.18)},${mix(g, 0.18)},${mix(b, 0.18)},1)`;
        if (role === 'report') return `rgba(${r},${g},${b},1)`;
        if (role === 'both') return `rgba(${mix(r, -0.45)},${mix(g, -0.45)},${mix(b, -0.45)},1)`;
        return null;
    }

    // In Resource Wise view, calendarData is already scoped to the selected resource, so
    // "overlap" means this resource is double-booked: this assignment's dates overlap
    // another assignment of theirs. Only THEN does the bar get tinted, and every assignment
    // in that overlapping group gets the SAME color — based on whether the resource is an
    // execute resource, report resource, or both across the overlapping set (execute wins
    // if mixed). No overlap at all means no tint — just the plain theme color.
    getOverlapRole(item, resourceName) {
        if (!resourceName) return null;
        const { calendarData } = this.state;
        const s1 = moment(item.startDate).startOf('day');
        const e1 = item.endDate ? moment(item.endDate).startOf('day') : s1;

        const overlapping = calendarData.filter(other => {
            if (other === item) return false;
            if (!other.startDate) return false;
            const s2 = moment(other.startDate).startOf('day');
            const e2 = other.endDate ? moment(other.endDate).startOf('day') : s2;
            return s1.isSameOrBefore(e2) && e1.isSameOrAfter(s2);
        });
        if (!overlapping.length) return null;

        const roles = [item, ...overlapping].map(a => getResourceRole(a, resourceName)).filter(Boolean);
        if (!roles.length) return null;
        const hasExecute = roles.includes('execute') || roles.includes('both');
        const hasReport = roles.includes('report') || roles.includes('both');
        if (hasExecute && hasReport) return 'both';
        if (hasExecute) return 'execute';
        if (hasReport) return 'report';
        return null;
    }

    renderMonthView() {
        const { currentDate, selectedDate } = this.state;
        const { themeSettings } = this.props;
        const themeColor = `rgba(${themeSettings.themeColor.r},${themeSettings.themeColor.g},${themeSettings.themeColor.b},${themeSettings.themeColor.a})`;
        const themeBg = `rgba(${themeSettings.themeColor.r},${themeSettings.themeColor.g},${themeSettings.themeColor.b},0.10)`;
        const resourceName = this.getSelectedResourceName();

        const startOfCal = moment(currentDate).startOf('month').startOf('isoWeek');
        const endOfCal = moment(currentDate).endOf('month').endOf('isoWeek');
        const allDays = [];
        let d = moment(startOfCal);
        while (d.isSameOrBefore(endOfCal)) { allDays.push(moment(d)); d.add(1, 'day'); }
        const weeks = [];
        for (let i = 0; i < allDays.length; i += 7) weeks.push(allDays.slice(i, i + 7));

        return (
            <div style={{ overflowX: 'auto', userSelect: 'none' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e8e8e8', background: '#fafafa' }}>
                    {DAY_HEADERS.map(h => (
                        <div key={h} style={{ textAlign: 'center', padding: '8px 4px', fontSize: 12, fontWeight: 600, color: '#888' }}>
                            {h}
                        </div>
                    ))}
                </div>

                {weeks.map((week, wi) => {
                    const weekStart = week[0];
                    const weekEnd = week[6];

                    const weekItems = this.getEventsForWeek(weekStart, weekEnd);
                    const positioned = positionEventsInWeek(weekItems, weekStart);
                    const maxLane = positioned.length ? Math.max(...positioned.map(e => e.lane)) : -1;
                    const rowHeight = Math.max(MIN_ROW_H, DATE_ROW_H + (maxLane + 1) * (BAR_HEIGHT + BAR_GAP) + 8);

                    return (
                        <div key={wi} style={{ position: 'relative', borderBottom: wi < weeks.length - 1 ? '1px solid #e8e8e8' : 'none' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', height: rowHeight }}>
                                {week.map((day, di) => {
                                    const isCurrentMonth = day.month() === currentDate.month();
                                    const isToday = day.isSame(moment(), 'day');
                                    const isSelected = selectedDate && day.isSame(selectedDate, 'day');
                                    const overlapColor = this.getOverlapColorForDay(day);
                                    const hasOverlap = !!overlapColor;

                                    const prevOverlapColor = di > 0 ? this.getOverlapColorForDay(week[di - 1]) : null;
                                    const nextOverlapColor = di < 6 ? this.getOverlapColorForDay(week[di + 1]) : null;
                                    const prevHasOverlap = !!prevOverlapColor && prevOverlapColor === overlapColor;
                                    const nextHasOverlap = !!nextOverlapColor && nextOverlapColor === overlapColor;

                                    let boxShadow = 'none';
                                    if (isSelected) {
                                        boxShadow = `inset 0 2px 0 ${themeColor}, inset 0 -2px 0 ${themeColor}, inset 2px 0 0 ${themeColor}, inset -2px 0 0 ${themeColor}`;
                                    } else if (hasOverlap) {
                                        const shadows = [`inset 0 2px 0 ${overlapColor}`, `inset 0 -2px 0 ${overlapColor}`];
                                        if (!prevHasOverlap) shadows.push(`inset 2px 0 0 ${overlapColor}`);
                                        if (!nextHasOverlap) shadows.push(`inset -2px 0 0 ${overlapColor}`);
                                        boxShadow = shadows.join(', ');
                                    }

                                    return (
                                        <div
                                            key={di}
                                            onClick={() => this.selectDate(day)}
                                            style={{
                                                borderRight: di < 6 ? '1px solid #e8e8e8' : 'none',
                                                padding: '5px 6px 0',
                                                background: isSelected ? themeBg : isToday ? themeBg : hasOverlap ? '#fffbf0' : '#fff',
                                                opacity: isCurrentMonth ? 1 : 0.38,
                                                cursor: 'pointer',
                                                boxSizing: 'border-box',
                                                boxShadow,
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    width: 26, height: 26, borderRadius: '50%', fontSize: 12,
                                                    fontWeight: (isToday || isSelected) ? 700 : 400,
                                                    color: (isToday || isSelected) ? '#fff' : '#333',
                                                    background: isSelected ? themeColor : isToday ? themeColor : 'transparent',
                                                }}>
                                                    {day.date()}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {positioned.map((ev, idx) => {
                                const { item, startCol, endCol, span, lane, isEventStart, isEventEnd } = ev;
                                const top = DATE_ROW_H + lane * (BAR_HEIGHT + BAR_GAP);
                                const left = `calc(${startCol * (100 / 7)}% + 3px)`;
                                const width = `calc(${span * (100 / 7)}% - 6px)`;

                                const roundLeft = isEventStart || startCol === 0;
                                const roundRight = isEventEnd || endCol === 6;
                                const borderRadius = roundLeft && roundRight ? 4
                                    : roundLeft ? '4px 0 0 4px'
                                    : roundRight ? '0 4px 4px 0'
                                    : 0;
                                const barColor = this.getRoleColor(this.getOverlapRole(item, resourceName)) || themeColor;

                                return (
                                    <Tooltip
                                        key={idx}
                                        title={this.renderTooltipContent(item)}
                                        color={this.getThemeColor()}
                                        placement="top"
                                        overlayInnerStyle={{ padding: '8px 10px' }}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top,
                                                left,
                                                width,
                                                height: BAR_HEIGHT,
                                                background: barColor,
                                                color: '#fff',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                borderRadius,
                                                padding: '3px 7px',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                textOverflow: 'ellipsis',
                                                cursor: 'default',
                                                zIndex: 1,
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            {this.getEventLabel(item)}
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderWeekView() {
        const { currentDate, selectedDate } = this.state;
        const { themeSettings } = this.props;
        const themeColor = `rgba(${themeSettings.themeColor.r},${themeSettings.themeColor.g},${themeSettings.themeColor.b},${themeSettings.themeColor.a})`;
        const themeBg = `rgba(${themeSettings.themeColor.r},${themeSettings.themeColor.g},${themeSettings.themeColor.b},0.08)`;
        const resourceName = this.getSelectedResourceName();

        const startOfWeek = moment(currentDate).startOf('isoWeek');
        const days = Array.from({ length: 7 }, (_, i) => moment(startOfWeek).add(i, 'days'));

        const positioned = positionEventsInWeek(this.getEventsForWeek(days[0], days[6]), days[0]);
        const maxLane = positioned.length ? Math.max(...positioned.map(e => e.lane)) : -1;
        const evAreaH = (maxLane + 1) * (BAR_HEIGHT + BAR_GAP) + 8;

        return (
            <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#fafafa', borderBottom: '2px solid #e8e8e8' }}>
                    {days.map((d, i) => {
                        const isToday = d.isSame(moment(), 'day');
                        const isSelected = selectedDate && d.isSame(selectedDate, 'day');
                        const overlapColor = this.getOverlapColorForDay(d);
                        const hasOverlap = !!overlapColor;
                        const prevOverlapColor = i > 0 ? this.getOverlapColorForDay(days[i - 1]) : null;
                        const nextOverlapColor = i < 6 ? this.getOverlapColorForDay(days[i + 1]) : null;
                        const prevHasOverlap = !!prevOverlapColor && prevOverlapColor === overlapColor;
                        const nextHasOverlap = !!nextOverlapColor && nextOverlapColor === overlapColor;

                        let boxShadow = 'none';
                        if (isSelected) {
                            boxShadow = `inset 0 2px 0 ${themeColor}, inset 2px 0 0 ${themeColor}, inset -2px 0 0 ${themeColor}`;
                        } else if (hasOverlap) {
                            const shadows = [`inset 0 2px 0 ${overlapColor}`];
                            if (!prevHasOverlap) shadows.push(`inset 2px 0 0 ${overlapColor}`);
                            if (!nextHasOverlap) shadows.push(`inset -2px 0 0 ${overlapColor}`);
                            boxShadow = shadows.join(', ');
                        }

                        return (
                            <div key={i}
                                onClick={() => this.selectDate(d)}
                                style={{
                                    textAlign: 'center', padding: '10px 8px',
                                    borderRight: i < 6 ? '1px solid #e8e8e8' : 'none',
                                    background: isSelected ? themeBg : isToday ? themeBg : hasOverlap ? '#fffbf0' : 'transparent',
                                    cursor: 'pointer',
                                    boxSizing: 'border-box',
                                    boxShadow,
                                }}>
                                <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>{d.format('ddd').toUpperCase()}</div>
                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 30, height: 30, borderRadius: '50%', fontSize: 15, fontWeight: 700, marginTop: 4,
                                    color: (isToday || isSelected) ? '#fff' : '#333',
                                    background: isSelected ? themeColor : isToday ? themeColor : 'transparent',
                                }}>
                                    {d.date()}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div style={{ position: 'relative', height: Math.max(MIN_ROW_H, evAreaH) }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', height: '100%', position: 'absolute', width: '100%' }}>
                        {days.map((d, i) => {
                            const isToday = d.isSame(moment(), 'day');
                            const isSelected = selectedDate && d.isSame(selectedDate, 'day');
                            const overlapColor = this.getOverlapColorForDay(d);
                            const hasOverlap = !!overlapColor;
                            const prevOverlapColor = i > 0 ? this.getOverlapColorForDay(days[i - 1]) : null;
                            const nextOverlapColor = i < 6 ? this.getOverlapColorForDay(days[i + 1]) : null;
                            const prevHasOverlap = !!prevOverlapColor && prevOverlapColor === overlapColor;
                            const nextHasOverlap = !!nextOverlapColor && nextOverlapColor === overlapColor;

                            let boxShadow = 'none';
                            if (isSelected) {
                                boxShadow = `inset 0 -2px 0 ${themeColor}, inset 2px 0 0 ${themeColor}, inset -2px 0 0 ${themeColor}`;
                            } else if (hasOverlap) {
                                const shadows = [`inset 0 -2px 0 ${overlapColor}`];
                                if (!prevHasOverlap) shadows.push(`inset 2px 0 0 ${overlapColor}`);
                                if (!nextHasOverlap) shadows.push(`inset -2px 0 0 ${overlapColor}`);
                                boxShadow = shadows.join(', ');
                            }

                            return (
                                <div key={i}
                                    onClick={() => this.selectDate(d)}
                                    style={{
                                        borderRight: i < 6 ? '1px solid #e8e8e8' : 'none',
                                        borderBottom: '1px solid #e8e8e8',
                                        background: isSelected ? themeBg : isToday ? themeBg : hasOverlap ? '#fffbf0' : '#fff',
                                        cursor: 'pointer',
                                        boxSizing: 'border-box',
                                        boxShadow,
                                    }}
                                />
                            );
                        })}
                    </div>

                    {positioned.map((ev, idx) => {
                        const { item, startCol, endCol, span, lane, isEventStart, isEventEnd } = ev;
                        const top = lane * (BAR_HEIGHT + BAR_GAP) + 6;
                        const left = `calc(${startCol * (100 / 7)}% + 3px)`;
                        const width = `calc(${span * (100 / 7)}% - 6px)`;
                        const roundLeft = isEventStart || startCol === 0;
                        const roundRight = isEventEnd || endCol === 6;
                        const borderRadius = roundLeft && roundRight ? 4
                            : roundLeft ? '4px 0 0 4px'
                            : roundRight ? '0 4px 4px 0'
                            : 0;
                        const barColor = this.getRoleColor(this.getOverlapRole(item, resourceName)) || themeColor;

                        return (
                            <Tooltip key={idx} title={this.renderTooltipContent(item)} color={this.getThemeColor()} placement="top" overlayInnerStyle={{ padding: '8px 10px' }}>
                                <div
                                    style={{
                                        position: 'absolute', top, left, width,
                                        height: BAR_HEIGHT,
                                        background: barColor, color: '#fff',
                                        borderRadius, padding: '3px 7px',
                                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                                        fontSize: 11, fontWeight: 600,
                                        cursor: 'default', zIndex: 1, boxSizing: 'border-box',
                                    }}
                                >
                                    {this.getEventLabel(item)}
                                </div>
                            </Tooltip>
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        const { currentDate, viewType, loading, viewFilter, selectedResourceId, resourceList, resourceLoading } = this.state;
        const { themeSettings, onBack } = this.props;
        const themeColor = `rgba(${themeSettings.themeColor.r},${themeSettings.themeColor.g},${themeSettings.themeColor.b},${themeSettings.themeColor.a})`;

        return (
            <div style={{ background: '#fff', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #e8e8e8', background: '#fafafa', flexWrap: 'nowrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {onBack && (
                            <button onClick={onBack} style={{ ...navBtnStyle, padding: '3px 10px', gap: 4, color: themeColor, borderColor: themeColor }}>
                                <span className="material-icons" style={{ fontSize: 16 }}>arrow_back</span>
                                <span style={{ fontSize: 12, fontWeight: 600 }}>Back</span>
                            </button>
                        )}
                        <button onClick={this.prevPeriod} style={navBtnStyle}>
                            <span className="material-icons" style={{ fontSize: 18 }}>chevron_left</span>
                        </button>
                        <span style={{ fontSize: 15, fontWeight: 600, minWidth: 180, textAlign: 'center' }}>{currentDate.format('MMMM YYYY')}</span>
                        <button onClick={this.nextPeriod} style={navBtnStyle}>
                            <span className="material-icons" style={{ fontSize: 18 }}>chevron_right</span>
                        </button>
                        <button onClick={this.goToToday} style={{ ...navBtnStyle, padding: '3px 10px', fontSize: 12 }}>Today</button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', flexShrink: 0 }}>
                        <FilterDropDown
                            placeholder={viewFilter === 'assignment' ? 'Assignment Wise' : 'Resource Wise'}
                            dropdownArr={[
                                { label: 'Assignment Wise', value: 'assignment', isSelected: viewFilter === 'assignment' },
                                { label: 'Resource Wise', value: 'resource', isSelected: viewFilter === 'resource' },
                            ]}
                            onDropDownItemClick={(item) => this.setState({ viewFilter: item.value, selectedResourceId: null })}
                            themeSettings={themeSettings}
                            mleft={0}
                        />

                        {viewFilter === 'resource' && (() => {
                            const resourceOptions = resourceList.map(r => ({ ...r, resourceName: `${r.salutation} ${r.firstName} ${r.lastName}`.trim() }));
                            const selectedResourceOption = resourceOptions.find(r => r.userId === selectedResourceId) || null;
                            return (
                                <div style={{ minWidth: 220, marginLeft: 10 }}>
                                    <ReactSelectDropdown
                                        selectedDDObj={selectedResourceOption}
                                        handleReactSelectClose={(item) => this.setState({ selectedResourceId: item ? item.userId : null })}
                                        selectDropdownArr={resourceOptions}
                                        label={"resourceName"}
                                        value={"userId"}
                                        placeholder={selectedResourceOption ? selectedResourceOption.resourceName : "Select Resource"}
                                        isClearSelect={true}
                                        disabled={resourceLoading}
                                    />
                                </div>
                            );
                        })()}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        <button onClick={() => this.setState({ viewFilter: 'assignment', selectedResourceId: null, resourceList: [] }, this.fetchCalendarData)} style={{ ...navBtnStyle, padding: '3px 8px' }} title="Refresh">
                            <span className="material-icons" style={{ fontSize: 16 }}>refresh</span>
                        </button>
                        <div style={{ display: 'flex', border: `1px solid ${themeColor}`, borderRadius: 4, overflow: 'hidden' }}>
                            {['month', 'week'].map(v => (
                                <button key={v} onClick={() => this.setState({ viewType: v })} style={{ padding: '4px 14px', fontSize: 13, cursor: 'pointer', border: 'none', background: viewType === v ? themeColor : '#fff', color: viewType === v ? '#fff' : themeColor, fontWeight: viewType === v ? 600 : 400, transition: 'all 0.15s' }}>
                                    {v === 'month' ? 'Monthly' : 'Weekly'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {viewFilter === 'resource' && selectedResourceId &&
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '6px 16px', borderBottom: '1px solid #e8e8e8', background: '#fafafa', fontSize: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: this.getRoleColor('execute'), display: 'inline-block' }} />
                            Execute Resource
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: this.getRoleColor('report'), display: 'inline-block' }} />
                            Report Resource
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: this.getRoleColor('both'), display: 'inline-block' }} />
                            Both
                        </div>
                    </div>
                }

                <Spin spinning={loading} tip="Loading...">
                    <div style={{ padding: 0, minHeight: 300 }}>
                        {viewType === 'month' ? this.renderMonthView() : this.renderWeekView()}
                    </div>
                </Spin>
            </div>
        );
    }
}

const navBtnStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', padding: '3px 6px' };

export default AssignmentCalendarView;
